import {
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { useSearchParams } from "react-router";

import {
  KNOWLEDGE_REPOSITORIES_QUERY_KEY,
  type KnowledgeRepository,
  knowledgeRepositoriesApi,
} from "./knowledge-repositories";
import { selectedRepositoryId } from "./repository-scope";

const SCOPED_QUERY_ROOTS = [
  "sources",
  "sources-count",
  "source-full",
  "source-tags",
  "validation",
  "knowledge-findings",
  "knowledge-usage",
  "knowledge-curation",
  "sensitive-data",
] as const;

export interface KnowledgeRepositorySelection {
  repositories: readonly KnowledgeRepository[];
  selected: KnowledgeRepository | null;
  selectedId: string | null;
  scopeKey: string;
  isArchived: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  /**
   * False only while a `?repository=` id from the URL is still unvalidated
   * (the repository list hasn't resolved yet, so we can't tell whether it
   * names a real repository). Callers that scope a query by `scopeKey`
   * should gate it on this — otherwise a bad or stale deep link fires the
   * query against the raw, unvalidated id and the backend's 404 flashes on
   * screen before the self-correcting effect below strips the param. When
   * there is no `?repository=` param at all there is nothing to validate,
   * so this stays `true` from the first render and no legitimate load is
   * delayed.
   */
  isReady: boolean;
  setSelectedId: (id: string) => void;
}

export function useKnowledgeRepositorySelection({
  includeArchived,
  enabled = true,
  activeOnly = false,
  hideArchivedFromPicker = false,
}: {
  includeArchived: boolean;
  /**
   * Set to `false` when the caller only needs this hook to validate and
   * self-correct an *already-present* `?repository=` id rather than to list
   * every repository (e.g. a nav badge, not a repository picker). Callers
   * that render a picker from `repositories` must leave this at the
   * default so the list loads unconditionally.
   */
  enabled?: boolean;
  /**
   * Drop archived repositories from the *selectable* set without changing
   * what's fetched. Set this — with `includeArchived: true` — when the page
   * has no read-only story for an archived repository at all but still
   * wants `includeArchived: true`'s query key so it dedupes against a
   * sibling caller's fetch (e.g. the settings sidebar) instead of firing a
   * second, differently-scoped request for the same list (this is
   * `knowledge-quality.tsx`'s case). An archived repository is then treated
   * exactly as `includeArchived: false` would have treated it: absent from
   * `repositories`, never resolved from `?repository=`, and a deep link
   * naming one self-corrects away like any other invalid id.
   *
   * For a page that *does* keep a read-only story for an
   * already-referenced archived repository (`/sources`, `/validator`), use
   * `hideArchivedFromPicker` instead — it hides archived repositories from
   * the offered options without breaking a `?repository=` deep link that
   * already names one.
   */
  activeOnly?: boolean;
  /**
   * Hide archived repositories from the *offered* picker options
   * (`repositories`, and therefore `defaultRepository`) without touching
   * resolution: a `?repository=` id that already names an archived
   * repository still resolves and is returned as `selected`, so a page with
   * a read-only "archived" view for it (`/sources`, `/validator`) keeps
   * working — the repository just isn't something a user can newly pick
   * from the dropdown.
   */
  hideArchivedFromPicker?: boolean;
}): KnowledgeRepositorySelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [...KNOWLEDGE_REPOSITORIES_QUERY_KEY, { includeArchived }] as const,
    queryFn: () => knowledgeRepositoriesApi.list(includeArchived),
    enabled,
  });
  const requestedId = selectedRepositoryId(searchParams.toString());
  const fetched = query.data ?? [];
  const repositories =
    activeOnly || hideArchivedFromPicker
      ? fetched.filter((repository) => repository.archived_at === null)
      : fetched;
  const defaultRepository = repositories.find((repository) => repository.is_default) ?? null;
  // `activeOnly` resolves against the filtered `repositories` (an archived
  // id is treated as invalid, same as `includeArchived: false`);
  // `hideArchivedFromPicker` resolves against the full, unfiltered `fetched`
  // list so an already-referenced archived repository still resolves for
  // its read-only view even though it's absent from the picker's options.
  const resolvable = activeOnly ? repositories : fetched;
  const requestedRepository =
    requestedId === null
      ? null
      : (resolvable.find((repository) => repository.id === requestedId) ?? null);
  const selected = requestedRepository ?? defaultRepository;

  // An inaccessible, archived (when excluded), or malformed selection must
  // not keep scoping operational calls to a repository the page cannot use.
  useEffect(() => {
    if (!query.isSuccess || requestedId === null || requestedRepository !== null) return;
    const next = new URLSearchParams(searchParams);
    next.delete("repository");
    setSearchParams(next, { replace: true });
  }, [query.isSuccess, requestedId, requestedRepository, searchParams, setSearchParams]);

  function setSelectedId(id: string): void {
    const next = new URLSearchParams(searchParams);
    if (id === defaultRepository?.id) next.delete("repository");
    else next.set("repository", id);
    setSearchParams(next, { replace: true });
    for (const root of SCOPED_QUERY_ROOTS) {
      void queryClient.invalidateQueries({ queryKey: [root] });
    }
  }

  // `selected` already falls back requestedRepository -> defaultRepository,
  // so this is the single source of truth for "what is actually resolved
  // right now" — `scopeKey` must track it instead of re-deriving its own
  // fallback chain, or an invalid `requestedId` leaves the two disagreeing
  // (the cache then keys queries under an id no repository ever had).
  const resolvedId = selected?.id ?? requestedId;
  // Nothing to validate when the URL carries no `repository` param; when it
  // does, wait for the list query to settle (success or error) before
  // trusting `resolvedId` — otherwise it's still the raw, unvalidated id.
  const isReady = requestedId === null || query.isSuccess || query.isError;

  return {
    repositories,
    selected,
    selectedId: resolvedId,
    scopeKey: resolvedId ?? "default",
    isArchived: selected?.archived_at !== null && selected?.archived_at !== undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    isReady,
    setSelectedId,
  };
}

// A type alias (not `interface ... extends`) because `UseQueryResult` is a
// discriminated union of the query's possible states — an interface can't
// extend a union, but a `&` intersection distributes over it correctly,
// keeping `data`/`isSuccess`/etc. narrowed exactly as they are on the
// wrapped `useQuery` result.
export type ScopedQueryResult<TData, TError> = UseQueryResult<TData, TError> & {
  /**
   * True while `selection.isReady` is false — i.e. this query is being held
   * back because a `?repository=` id from the URL hasn't been validated yet.
   * React Query reports a disabled query's `isLoading` as `false` (it only
   * turns `true` once fetching actually starts), so a loading-state check
   * that only reads `isLoading` misses this window entirely. Combine the
   * two in JSX: `query.isLoading || query.isBlockedByScope`.
   */
  isBlockedByScope: boolean;
};

/**
 * `useQuery`, but gated on a `KnowledgeRepositorySelection`'s `isReady` so the
 * scope race described on `isReady` above cannot be reintroduced by a call
 * site forgetting to wire it into `enabled`. Pass any extra `enabled`
 * condition via `options.enabled` — it's ANDed with `selection.isReady`
 * rather than overridden.
 *
 * Use this instead of a raw `useQuery` for anything scoped to the selected
 * knowledge repository (i.e. whose `queryFn` or `queryKey` depends on
 * `selection.scopeKey`, or hits an endpoint that reads the ambient
 * `?repository=` off the URL). A query that doesn't depend on the selected
 * repository at all has no race to guard against and can stay a plain
 * `useQuery`.
 */
export function useScopedQuery<TQueryFnData, TError = Error, TData = TQueryFnData>(
  selection: Pick<KnowledgeRepositorySelection, "isReady">,
  options: UseQueryOptions<TQueryFnData, TError, TData>,
): ScopedQueryResult<TData, TError> {
  const query = useQuery({
    ...options,
    enabled: selection.isReady && (options.enabled ?? true),
  });
  return { ...query, isBlockedByScope: !selection.isReady };
}
